import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { FaArrowLeft, FaTrash, FaTimes, FaCheck } from 'react-icons/fa'
import { useDispatch, useSelector } from 'react-redux'
import { fetchBids, acceptBid, rejectBid, cancelBid } from '../../store/reducers/bidSlice'
import type { RootState, AppDispatch } from '../../store/store'

import './MyBidsPage.css'

type TabType = 'owner' | 'bidder'

export default function MyBidsPage() {
  const navigate = useNavigate()
  const dispatch = useDispatch<AppDispatch>()
  const {
    bidList,
    totalCount,
    isLoading,
    error,
    isAccepting,
    isRejecting,
    isCancelling
  } = useSelector((s: RootState) => s.bids)

  const [tab, setTab] = useState<TabType>('owner')

  useEffect(() => {
    dispatch(fetchBids({ type: tab, limit: 50, offset: 0 }))
  }, [dispatch, tab])

  const handleAccept = (bidId: string) => {
    dispatch(acceptBid({ bidId }))
      .unwrap()
      .then(() => dispatch(fetchBids({ type: tab, limit: 50, offset: 0 })))
      .catch(err => alert(`Accept failed: ${err}`))
  }

  const handleDecline = (bidId: string) => {
    dispatch(rejectBid({ bidId }))
      .unwrap()
      .then(() => dispatch(fetchBids({ type: tab, limit: 50, offset: 0 })))
      .catch(err => alert(`Reject failed: ${err}`))
  }

  const handleCancel = (bidId: string) => {
    dispatch(cancelBid({ bidId }))
      .unwrap()
      .then(() => dispatch(fetchBids({ type: tab, limit: 50, offset: 0 })))
      .catch(err => alert(`Cancel failed: ${err}`))
  }

  return (
    <div className="bids-container">
      <div className="bids-card">
        <header className="bids-header">
          <FaArrowLeft
            className="bids-back-icon"
            onClick={() => navigate(-1)}
          />
          <h2 className="bids-title">My Bids</h2>
        </header>

        <div className="bids-body">
          <div className="bids-tabs">
            <div
              className="bids-slider"
              style={{
                transform: tab === 'owner' ? 'translateX(0%)' : 'translateX(100%)'
              }}
            />
            <button
              className={tab === 'owner' ? 'active' : ''}
              onClick={() => setTab('owner')}
            >
              Sell
            </button>
            <button
              className={tab === 'bidder' ? 'active' : ''}
              onClick={() => setTab('bidder')}
            >
              Buy
            </button>
          </div>

          {isLoading && <div className="bids-loading">Loading…</div>}
          {error && <div className="bids-error">Error: {error}</div>}
          {!isLoading && !error && totalCount === 0 && (
            <div className="bids-empty">No bids yet</div>
          )}

          {!isLoading && !error && totalCount > 0 && (
            <ul className="bids-list">
              {(tab === 'owner' ? bidList.filter(b => b.status === 'pending') : bidList).map(bid => (
                <li key={bid.bidId} className="bids-item">
                  {tab === 'owner' ? (
                    <>
                      <div className="bids-user">{bid.bidderDetails.fullName}</div>
                      <div className="bids-property">{bid.propertyDetails.name}</div>
                      <div className="bids-price">${bid.bidAmount.toLocaleString()}</div>
                      <div className="bids-action">
                        <button
                          className="btn-decline"
                          onClick={() => handleDecline(bid.bidId)}
                          disabled={isRejecting}
                        >
                          <FaTimes style={{ marginRight: 6 }} />
                          Decline
                        </button>
                        <button
                          className="btn-accept"
                          onClick={() => handleAccept(bid.bidId)}
                          disabled={isAccepting}
                        >
                          <FaCheck style={{ marginRight: 6 }} />
                          Accept
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="bids-property">{bid.propertyDetails.name}</div>
                      <div className="bids-price">${bid.bidAmount.toLocaleString()}</div>
                      <div className={`bids-status ${bid.status.toLowerCase()}`}>
                        {bid.status}
                      </div>
                      <div className="bids-action">
                        <button
                          className="btn-delete"
                          onClick={() => handleCancel(bid.bidId)}
                          disabled={isCancelling}
                        >
                          <FaTrash style={{ marginRight: 6 }} />
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  )
}